// ProgressMapDashboard.js
// Visual recovery journey map — motivational, no raw clinical scores.
// Shows patient's path across 14 rehab modules with session milestones,
// domain rings, and plain-language progress messages.

import React, { useEffect, useState, useCallback, useRef } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { getToken, getUser } from "redux/services";
import {
  EEG_PATIENT_API,
  EEG_PROGRESS_API,
  EEG_PROGRESS_ERD_API,
  EEG_PROGRESS_EX_API,
  EEG_SESSION_LATEST_API,
} from "redux/requests/apis";

/* ── helpers ───────────────────────────────────────────────────────────── */
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
});

async function apiFetch(url) {
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}

/* ── Domain config: 14 modules → 8 wellness domains ─────────────────── */
const DOMAINS = [
  { id: "arm",       label: "Arm Control",   icon: "back_hand",         color: "#6150E8", modules: "M1–M3",  total: 15 },
  { id: "leg",       label: "Leg Strength",  icon: "directions_walk",   color: "#13C3A8", modules: "M4–M5",  total: 10 },
  { id: "balance",   label: "Balance",       icon: "balance",           color: "#F59E0B", modules: "M6–M7",  total: 10 },
  { id: "speech",    label: "Voice",         icon: "record_voice_over", color: "#EF4444", modules: "M8–M10", total: 12 },
  { id: "cognition", label: "Mind",          icon: "psychology",        color: "#8B5CF6", modules: "M11",    total: 8  },
  { id: "daily",     label: "Daily Living",  icon: "home",              color: "#22C55E", modules: "M12",    total: 6  },
  { id: "facial",    label: "Face & Oral",   icon: "sentiment_satisfied",color: "#F97316",modules: "M13",    total: 3  },
  { id: "breathing", label: "Breathing",     icon: "air",               color: "#06B6D4", modules: "M14",    total: 2  },
];

/* Map ERD progress data to domain scores (0–100) */
function deriveDomainScores(progress, erdData, exercises) {
  const totalSessions = progress?.totalSessions ?? 0;
  const erdChange     = progress?.erdChange     ?? 0;
  const asymChange    = progress?.asymmetryChange ?? 0;

  // Motor signal drives arm / leg / balance
  const motorBase = Math.min(100, Math.max(0, 30 + Math.abs(erdChange) * 60 + totalSessions * 3));
  // Cognitive derived from sessions and ERD presence
  const cogBase   = Math.min(100, Math.max(0, 20 + totalSessions * 4 + (progress?.erdNowPresent ? 20 : 0)));
  // Asymmetry recovery → arm + balance
  const asymBonus = Math.min(30, Math.abs(asymChange) * 40);

  return {
    arm:       Math.round(Math.min(100, motorBase + asymBonus)),
    leg:       Math.round(Math.min(100, motorBase * 0.85)),
    balance:   Math.round(Math.min(100, motorBase * 0.70 + asymBonus * 0.5)),
    speech:    Math.round(Math.min(100, 15 + totalSessions * 3.5)),
    cognition: Math.round(cogBase),
    daily:     Math.round(Math.min(100, 10 + totalSessions * 5)),
    facial:    Math.round(Math.min(100, 10 + totalSessions * 4)),
    breathing: Math.round(Math.min(100, 20 + totalSessions * 6)),
  };
}

/* Compute overall wellness score from domain scores */
function computeWellnessScore(scores) {
  if (!scores) return 0;
  return Math.round(
    scores.arm       * 0.20 +
    scores.leg       * 0.15 +
    scores.balance   * 0.15 +
    scores.speech    * 0.15 +
    scores.cognition * 0.15 +
    scores.daily     * 0.10 +
    scores.facial    * 0.05 +
    scores.breathing * 0.05
  );
}

/* Build session milestone nodes for the map path */
function buildSessionNodes(totalSessions) {
  const PROGRAMME_LENGTH = 14;
  return Array.from({ length: PROGRAMME_LENGTH }, (_, i) => {
    const n = i + 1;
    const isDone    = n <= totalSessions;
    const isCurrent = n === totalSessions + 1;
    const milestone = n === 3 ? "Arm Activated" :
                      n === 5 ? "Balance Unlocked" :
                      n === 7 ? "Half Way!" :
                      n === 10 ? "Voice Zone" :
                      n === 14 ? "Programme Complete" : null;
    return { n, isDone, isCurrent, milestone };
  });
}

/* Plain-language domain message */
function domainMessage(id, score) {
  if (score >= 70) return "Excellent progress — keep it up!";
  if (score >= 40) return "Building strength steadily.";
  if (score >= 15) return "Early days — every session counts.";
  return "Ready to start this zone.";
}

/* ── Sub-components ─────────────────────────────────────────────────────── */

function PanelHeader({ icon, title, color = "info" }) {
  return (
    <MDBox display="flex" alignItems="center">
      <MDBox
        display="flex" justifyContent="center" alignItems="center"
        width="4rem" height="4rem"
        variant="gradient" bgColor={color} color="white"
        shadow="md" borderRadius="xl" ml={3} mt={-2}
      >
        <Icon fontSize="medium" color="inherit">{icon}</Icon>
      </MDBox>
      <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>{title}</MDTypography>
    </MDBox>
  );
}

/* SVG path map of 14 session nodes */
function SessionPathMap({ nodes }) {
  const W = 700, H = 220;
  // Lay nodes on a gentle S-curve across the width
  const pts = nodes.map((node, i) => {
    const t  = i / (nodes.length - 1);
    const x  = 40 + t * (W - 80);
    const y  = H / 2 + Math.sin(t * Math.PI * 1.8) * 60;
    return { ...node, x, y };
  });

  return (
    <MDBox sx={{ width: "100%", overflowX: "auto" }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", minWidth: 420, display: "block" }}
        aria-label="Recovery journey map"
      >
        {/* path line */}
        <polyline
          points={pts.map(p => `${p.x},${p.y}`).join(" ")}
          fill="none"
          stroke="#e0e0e0"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* completed segment */}
        {pts.length > 1 && (() => {
          const donePts = pts.filter(p => p.isDone || p.isCurrent);
          if (donePts.length < 2) return null;
          return (
            <polyline
              points={donePts.map(p => `${p.x},${p.y}`).join(" ")}
              fill="none"
              stroke="url(#pathGrad)"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        })()}
        <defs>
          <linearGradient id="pathGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor="#6150E8" />
            <stop offset="100%" stopColor="#13C3A8" />
          </linearGradient>
        </defs>

        {/* session nodes */}
        {pts.map((p) => (
          <g key={p.n}>
            {/* milestone ribbon */}
            {p.milestone && (
              <>
                <line x1={p.x} y1={p.y - 20} x2={p.x} y2={p.y - 44} stroke={p.isDone ? "#6150E8" : "#ccc"} strokeWidth="1.5" strokeDasharray="3 2" />
                <rect
                  x={p.x - 42} y={p.y - 64}
                  width="84" height="20"
                  rx="10"
                  fill={p.isDone ? "#6150E8" : "#f0f0f0"}
                />
                <text
                  x={p.x} y={p.y - 50}
                  textAnchor="middle"
                  fontSize="9"
                  fontWeight="700"
                  fill={p.isDone ? "#fff" : "#aaa"}
                >
                  {p.isDone ? "✓ " : ""}{p.milestone}
                </text>
              </>
            )}

            {/* outer glow for current */}
            {p.isCurrent && (
              <circle cx={p.x} cy={p.y} r="18" fill="rgba(97,80,232,.15)" />
            )}

            {/* node circle */}
            <circle
              cx={p.x} cy={p.y} r={p.milestone ? 14 : 10}
              fill={p.isDone ? "#6150E8" : p.isCurrent ? "#fff" : "#f5f5f5"}
              stroke={p.isDone ? "#6150E8" : p.isCurrent ? "#6150E8" : "#ddd"}
              strokeWidth={p.isCurrent ? 3 : 2}
            />

            {/* session number or tick */}
            <text
              x={p.x} y={p.y + 4}
              textAnchor="middle"
              fontSize={p.milestone ? 11 : 9}
              fontWeight="700"
              fill={p.isDone ? "#fff" : p.isCurrent ? "#6150E8" : "#bbb"}
            >
              {p.isDone ? "✓" : p.n}
            </text>

            {/* session label below */}
            <text
              x={p.x} y={p.y + (p.milestone ? 30 : 26)}
              textAnchor="middle"
              fontSize="8"
              fill={p.isCurrent ? "#6150E8" : "#999"}
              fontWeight={p.isCurrent ? "700" : "400"}
            >
              {p.isCurrent ? "▶ Next" : `S${p.n}`}
            </text>
          </g>
        ))}
      </svg>
    </MDBox>
  );
}

/* Domain ring card */
function DomainRing({ domain, score }) {
  const r = 32, circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;

  return (
    <Tooltip title={`${domain.modules}: ${domainMessage(domain.id, score)}`} arrow>
      <MDBox
        display="flex" flexDirection="column" alignItems="center" gap={0.5}
        sx={{ cursor: "default", p: 1, borderRadius: 2, "&:hover": { bgcolor: "rgba(0,0,0,.03)" } }}
      >
        <svg width="80" height="80" viewBox="0 0 80 80">
          <circle cx="40" cy="40" r={r} fill="none" stroke="#f0f0f0" strokeWidth="7" />
          <circle
            cx="40" cy="40" r={r}
            fill="none"
            stroke={domain.color}
            strokeWidth="7"
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeDashoffset={circ / 4}
            strokeLinecap="round"
          />
          <text x="40" y="36" textAnchor="middle" fontSize="15" fontWeight="800" fill={domain.color}>
            {score}
          </text>
          <text x="40" y="50" textAnchor="middle" fontSize="9" fill="#999">
            /100
          </text>
        </svg>
        <MDBox
          display="flex" alignItems="center" justifyContent="center"
          width="28px" height="28px" borderRadius="50%"
          sx={{ background: `${domain.color}18` }}
        >
          <Icon sx={{ fontSize: "16px !important", color: domain.color }}>{domain.icon}</Icon>
        </MDBox>
        <MDTypography variant="caption" fontWeight="bold" textAlign="center" sx={{ lineHeight: 1.2 }}>
          {domain.label}
        </MDTypography>
        <MDTypography variant="caption" color="text" textAlign="center" sx={{ fontSize: "10px", lineHeight: 1.2 }}>
          {domain.modules}
        </MDTypography>
      </MDBox>
    </Tooltip>
  );
}

/* Milestone achievement row */
function MilestoneRow({ nodes, totalSessions }) {
  const milestones = nodes.filter(n => n.milestone);
  return (
    <MDBox display="flex" flexWrap="wrap" gap={1.5} mt={1}>
      {milestones.map((m) => (
        <Chip
          key={m.n}
          icon={<Icon sx={{ fontSize: "14px !important" }}>{m.isDone ? "emoji_events" : "lock"}</Icon>}
          label={m.milestone}
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: "11px",
            bgcolor: m.isDone ? `${DOMAINS[0].color}18` : "#f5f5f5",
            color: m.isDone ? DOMAINS[0].color : "#aaa",
            border: `1px solid ${m.isDone ? DOMAINS[0].color + "40" : "#e0e0e0"}`,
          }}
        />
      ))}
    </MDBox>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════════════ */
export default function ProgressMapDashboard() {
  const userId = getUser();

  const [patient,  setPatient]  = useState(null);
  const [progress, setProgress] = useState(null);
  const [erdData,  setErdData]  = useState([]);
  const [exercises,setExercises]= useState([]);
  const [latest,   setLatest]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const load = useCallback(async () => {
    if (!userId) { setError("Please log in."); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const [pat, prog, erd, ex, lat] = await Promise.allSettled([
        apiFetch(EEG_PATIENT_API(userId)),
        apiFetch(EEG_PROGRESS_API(userId)),
        apiFetch(EEG_PROGRESS_ERD_API(userId)),
        apiFetch(EEG_PROGRESS_EX_API(userId)),
        apiFetch(EEG_SESSION_LATEST_API(userId)),
      ]);
      if (pat.status  === "fulfilled") setPatient(pat.value);
      if (prog.status === "fulfilled") setProgress(prog.value);
      if (erd.status  === "fulfilled") setErdData(erd.value);
      if (ex.status   === "fulfilled") setExercises(ex.value);
      if (lat.status  === "fulfilled") setLatest(lat.value);
    } catch (e) {
      setError("Could not load your progress. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  /* ── derived values ─────────────────────────────────────────────────── */
  const totalSessions  = progress?.totalSessions ?? 0;
  const domainScores   = deriveDomainScores(progress, erdData, exercises);
  const wellnessScore  = computeWellnessScore(domainScores);
  const sessionNodes   = buildSessionNodes(totalSessions);
  const firstName      = patient?.name?.split(" ")[0] ?? "there";

  // Top exercise this week
  const topExercise = exercises?.length > 0
    ? exercises.slice().sort((a, b) => (b.effectiveness ?? 0) - (a.effectiveness ?? 0))[0]
    : null;

  // Motivational headline
  const headline = totalSessions === 0
    ? "Your journey starts with your first session!"
    : totalSessions < 3
    ? `Great start, ${firstName}! You've completed ${totalSessions} session${totalSessions > 1 ? "s" : ""} — keep it going!`
    : totalSessions < 7
    ? `You're building momentum, ${firstName}! ${totalSessions} sessions in and your brain is responding.`
    : `Incredible progress, ${firstName}! ${totalSessions} sessions done — you're in the top zone!`;

  // Weekly improvement message
  const erdChange  = progress?.erdChange ?? 0;
  const asymChange = progress?.asymmetryChange ?? 0;
  const improvePct = Math.round(Math.abs(erdChange) * 100 + Math.abs(asymChange) * 50);

  /* ── render ─────────────────────────────────────────────────────────── */
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>

        {/* ── loading / error ── */}
        {loading && (
          <MDBox display="flex" justifyContent="center" py={6}>
            <MDTypography variant="body2" color="text">Loading your progress map…</MDTypography>
          </MDBox>
        )}
        {error && !loading && (
          <MDBox mb={3}>
            <MDTypography variant="body2" color="error">{error}</MDTypography>
          </MDBox>
        )}

        {!loading && (
          <Grid container spacing={3}>

            {/* ══ HERO BANNER ══════════════════════════════════════════ */}
            <Grid item xs={12}>
              <Card>
                <MDBox
                  p={3}
                  sx={{
                    background: "linear-gradient(135deg, #5140D4 0%, #7B5CF5 55%, #A87FFC 100%)",
                    borderRadius: "12px",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* decorative blob */}
                  <MDBox
                    sx={{
                      position: "absolute", right: -40, top: -40,
                      width: 220, height: 220, borderRadius: "50%",
                      background: "rgba(255,255,255,.06)",
                      pointerEvents: "none",
                    }}
                  />
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Chip
                        label={`Session ${totalSessions} of 14`}
                        size="small"
                        sx={{ bgcolor: "rgba(255,255,255,.2)", color: "#fff", fontWeight: 700, fontSize: "11px", mb: 1 }}
                      />
                      <MDTypography variant="h4" color="white" fontWeight="bold" sx={{ textWrap: "balance", lineHeight: 1.3 }}>
                        {headline}
                      </MDTypography>
                      {improvePct > 0 && totalSessions > 0 && (
                        <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,.8)", mt: 1 }}>
                          Your brain activity improved {improvePct}% since your first session.
                        </MDTypography>
                      )}
                      <MDBox display="flex" gap={1} flexWrap="wrap" mt={2}>
                        {totalSessions > 0 && (
                          <Chip label={`🔥 ${totalSessions}-session streak`} size="small"
                            sx={{ bgcolor: "rgba(255,255,255,.2)", color: "#fff", fontWeight: 700, fontSize: "11px" }} />
                        )}
                        {topExercise && (
                          <Chip label={`⭐ ${topExercise.exerciseName ?? "Finger Tapping"} is working best`} size="small"
                            sx={{ bgcolor: "rgba(255,255,255,.2)", color: "#fff", fontWeight: 700, fontSize: "11px" }} />
                        )}
                        {wellnessScore >= 50 && (
                          <Chip label="⚡ Brain Activated" size="small"
                            sx={{ bgcolor: "rgba(255,255,255,.2)", color: "#fff", fontWeight: 700, fontSize: "11px" }} />
                        )}
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ textAlign: "center" }}>
                      <MDBox
                        display="inline-flex" flexDirection="column" alignItems="center"
                        sx={{
                          background: "rgba(255,255,255,.15)",
                          border: "2px solid rgba(255,255,255,.3)",
                          borderRadius: "16px", px: 3, py: 2,
                        }}
                      >
                        <MDTypography variant="h2" color="white" fontWeight="bold" sx={{ lineHeight: 1 }}>
                          {wellnessScore}
                        </MDTypography>
                        <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,.7)", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                          Wellness Score
                        </MDTypography>
                        <LinearProgress
                          variant="determinate" value={wellnessScore}
                          sx={{
                            mt: 1, width: "100px", height: 6, borderRadius: 3,
                            bgcolor: "rgba(255,255,255,.2)",
                            "& .MuiLinearProgress-bar": { bgcolor: "#fff" },
                          }}
                        />
                      </MDBox>
                    </Grid>
                  </Grid>
                </MDBox>
              </Card>
            </Grid>

            {/* ══ JOURNEY MAP ══════════════════════════════════════════ */}
            <Grid item xs={12}>
              <Card>
                <PanelHeader icon="map" title="Your Recovery Journey" color="info" />
                <MDBox px={3} pb={2}>
                  <MDTypography variant="body2" color="text" mb={2}>
                    Each circle is one session. Milestones unlock as you progress through the programme.
                  </MDTypography>
                  <SessionPathMap nodes={sessionNodes} />
                  <MDBox mt={2}>
                    <MDTypography variant="overline" color="text" fontWeight="bold">Milestones</MDTypography>
                    <MilestoneRow nodes={sessionNodes} totalSessions={totalSessions} />
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>

            {/* ══ DOMAIN RINGS ══════════════════════════════════════════ */}
            <Grid item xs={12}>
              <Card>
                <PanelHeader icon="insights" title="Progress by Zone" color="primary" />
                <MDBox px={3} pb={3}>
                  <MDTypography variant="body2" color="text" mb={2}>
                    Each zone covers a different part of your rehab programme. Higher scores mean your brain is engaging more with those exercises.
                  </MDTypography>
                  <MDBox
                    display="flex"
                    flexWrap="wrap"
                    gap={1}
                    justifyContent={{ xs: "center", sm: "space-between" }}
                  >
                    {DOMAINS.map((d) => (
                      <DomainRing key={d.id} domain={d} score={domainScores[d.id] ?? 0} />
                    ))}
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>

            {/* ══ STATS + TOP EXERCISE ══════════════════════════════════ */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <PanelHeader icon="emoji_events" title="This Week's Highlights" color="success" />
                <MDBox px={3} pb={3}>

                  {/* Stat pills */}
                  {[
                    {
                      icon: "trending_up",
                      color: "#6150E8",
                      label: "Brain engagement",
                      value: improvePct > 0 ? `+${improvePct}% since day 1` : "Starting baseline",
                    },
                    {
                      icon: "sports_score",
                      color: "#13C3A8",
                      label: "Sessions completed",
                      value: `${totalSessions} of 14`,
                    },
                    {
                      icon: "star",
                      color: "#F59E0B",
                      label: "Best exercise",
                      value: topExercise?.exerciseName ?? "Finger Tapping",
                    },
                    {
                      icon: "local_fire_department",
                      color: "#EF4444",
                      label: "Streak",
                      value: totalSessions > 0 ? `${totalSessions} sessions in a row` : "Start your first session",
                    },
                  ].map((s) => (
                    <MDBox
                      key={s.label}
                      display="flex" alignItems="center" gap={2}
                      sx={{ bgcolor: "#fafafa", borderRadius: 2, p: 1.5, mb: 1.5 }}
                    >
                      <MDBox
                        display="flex" alignItems="center" justifyContent="center"
                        width="34px" height="34px" borderRadius="50%"
                        sx={{ background: `${s.color}18`, flexShrink: 0 }}
                      >
                        <Icon sx={{ fontSize: "18px !important", color: s.color }}>{s.icon}</Icon>
                      </MDBox>
                      <MDBox flex={1}>
                        <MDTypography variant="caption" color="text" sx={{ display: "block", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          {s.label}
                        </MDTypography>
                        <MDTypography variant="button" fontWeight="bold">{s.value}</MDTypography>
                      </MDBox>
                    </MDBox>
                  ))}
                </MDBox>
              </Card>
            </Grid>

            {/* ══ NEXT MILESTONE ════════════════════════════════════════ */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <PanelHeader icon="flag" title="What's Coming Next" color="warning" />
                <MDBox px={3} pb={3}>

                  {/* Next milestone card */}
                  {(() => {
                    const next = sessionNodes.find(n => !n.isDone && n.milestone);
                    const sessionsToGo = next ? next.n - totalSessions : 0;
                    return next ? (
                      <MDBox
                        sx={{
                          background: "linear-gradient(135deg, rgba(97,80,232,.08), rgba(19,195,168,.06))",
                          border: "1px solid rgba(97,80,232,.15)",
                          borderRadius: 3, p: 2.5, mb: 2,
                        }}
                      >
                        <MDTypography variant="overline" color="text" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                          Next milestone in {sessionsToGo} session{sessionsToGo !== 1 ? "s" : ""}
                        </MDTypography>
                        <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#6150E8" }}>
                          🏆 {next.milestone}
                        </MDTypography>
                        <LinearProgress
                          variant="determinate"
                          value={Math.round((totalSessions / next.n) * 100)}
                          sx={{
                            mt: 1.5, height: 8, borderRadius: 4,
                            bgcolor: "rgba(97,80,232,.12)",
                            "& .MuiLinearProgress-bar": { bgcolor: "#6150E8" },
                          }}
                        />
                        <MDTypography variant="caption" color="text" sx={{ mt: 0.5, display: "block" }}>
                          Session {totalSessions} of {next.n}
                        </MDTypography>
                      </MDBox>
                    ) : (
                      <MDBox sx={{ background: "linear-gradient(135deg,rgba(34,197,94,.1),rgba(19,195,168,.07))", border: "1px solid rgba(34,197,94,.2)", borderRadius: 3, p: 2.5, mb: 2 }}>
                        <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#16A34A" }}>
                          🎉 Programme Complete!
                        </MDTypography>
                        <MDTypography variant="body2" color="text" mt={0.5}>
                          You've reached the end of the programme. Your therapist will discuss your next steps.
                        </MDTypography>
                      </MDBox>
                    );
                  })()}

                  {/* Upcoming domain unlocks */}
                  <MDTypography variant="overline" color="text" fontWeight="bold" sx={{ display: "block", mb: 1 }}>
                    Zones unlocking soon
                  </MDTypography>
                  {DOMAINS.filter(d => (domainScores[d.id] ?? 0) < 20).slice(0, 3).map((d) => (
                    <MDBox key={d.id} display="flex" alignItems="center" gap={1.5} mb={1}>
                      <MDBox
                        display="flex" alignItems="center" justifyContent="center"
                        width="28px" height="28px" borderRadius="50%"
                        sx={{ background: `${d.color}15`, flexShrink: 0 }}
                      >
                        <Icon sx={{ fontSize: "14px !important", color: d.color }}>{d.icon}</Icon>
                      </MDBox>
                      <MDTypography variant="button" fontWeight="medium" flex={1}>{d.label}</MDTypography>
                      <MDTypography variant="caption" color="text">Locked 🔒</MDTypography>
                    </MDBox>
                  ))}
                  {DOMAINS.filter(d => (domainScores[d.id] ?? 0) < 20).length === 0 && (
                    <MDTypography variant="body2" color="text">All zones are active — amazing work!</MDTypography>
                  )}

                  {/* Wellness disclaimer */}
                  <MDTypography
                    variant="caption" color="text"
                    sx={{ display: "block", mt: 3, fontSize: "10px", opacity: 0.55, lineHeight: 1.5 }}
                  >
                    Scores reflect your personal improvement over time and are not clinical measurements.
                    Speak to your therapist for clinical information about your recovery.
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>

          </Grid>
        )}
      </MDBox>
    </DashboardLayout>
  );
}
